import { cartItemReferenceStoreAPI } from '../../stores/cart-item-reference-store.public-api';
import type { CartItemLocalInterface, CartItemReference, CartItemReferenceLogic } from '../../models';
import { uuidGenerator } from '../../utils/uuid-generator';
import { NewCartItemIdGenerator } from '../../utils/new-cart-item-id-generator';
import { ActionType as ActionTypeEnum } from '../../models/types/cart-item-reference-action';
import type { Observable } from '@legendapp/state';
import type { CartItemInterface } from '../../types/front.graphql';
import { type ProductModel } from '@kidslabs/module-catalog';
import { cartStoreAPI } from '../../stores/cart-store.public-api';
import { useComputed, useSelector } from '@legendapp/state/react';
import { getCartItemSku } from '../../helpers';

export interface UseCartItemReference {
    cartItemReferences: CartItemReference[];
    addCartItemReference: (ref: CartItemReference) => void;
    updateCartItemReference: (ref: CartItemReference) => void;
    removeCartItemReference: (cartItemReferenceId: string) => void;
    findCartItemReference: (productId: number, logics?: CartItemReferenceLogic[]) => CartItemReference | undefined;
    setCartItemReferences: (refs: CartItemReference[]) => void;
    initCartItemReference: (input: InitCartItemReferenceInput) => CartItemReference;
    isPromotionReference: (ref: CartItemReference) => boolean;
    references$: () => Observable<CartItemReference[]>;
    getCartItemReferencesWithCartItems: (cartItems: CartItemInterface[]) => CartItemReference[];
    getReferenceById: (cartItemReferenceId: string) => CartItemReference | undefined;
    addLogicsToCartItemReference: (cartItemReferenceId: string, newLogics: CartItemReferenceLogic[]) => void;
    setCartItemReferenceSelected: (cartItemReferenceId: string, selected: boolean) => void;
    toggleCartItemReferenceSelected: (cartItemReferenceId: string) => void;
    unselectedAllCartItemReferenceSelected: () => void;
    resetCartItemReferencesForNewCart: (newCartId: string) => void;
    validateAndFixCartItemReferences: (currentCartId: string) => void;
    getCartItemReferencesByProductId: (productId: number) => CartItemReference[];
    checkAvailableToBuyCartItemReference: (cartItemReference: CartItemReference) => boolean;
    getAvailableToBuyCartItemReferences: () => CartItemReference[];
    /** Find first item by product ID without gift logics */
    itemByProductIdWithoutGifts$: Observable<(productId: number) => CartItemLocalInterface | undefined>;
    hasSelectedLogics: () => boolean;
    removeSelectedLogics: () => void;
    getCartItemsHasInvalidLogics: () => CartItemReference[];
    removeInvalidSelectedLogics: () => void;
}

export interface InitCartItemReferenceInput {
    productId: number;
    cartItemId?: string;
    cartId?: string; // Add cartId to track cart ownership
    qty: number;
    // Logics should be an array of CartItemReferenceLogic, each with its own actions - this is the multiple gift rules
    logics: CartItemReferenceLogic[];
    createdAt?: string;
    cartItemReferenceId?: string; // Optional, can be generated if not provided
    isSelected?: boolean; // Cart items from server should be selected by default
    parentProduct?: ProductModel; // in case of configurable product, we store the parent product model
    addToStore?: boolean; // Whether to add the initialized reference to store (default true)
}

export const useCartItemReference = (): UseCartItemReference => {
    const getCartItemReferences = () => cartItemReferenceStoreAPI.state.getItems() as CartItemReference[];

    const setCartItemReferences = (refs: CartItemReference[]) => {
        console.log('Setting cart item references:', refs);
        cartItemReferenceStoreAPI.actions.setItems(refs);
    };

    const addCartItemReference = (ref: CartItemReference) => {
        const idx = getCartItemReferences().findIndex(r => r.cartItemReferenceId === ref.cartItemReferenceId);
        if (idx !== -1) {
            const existing = getCartItemReferences()[idx];
            // If already has logics (received gift), DO NOT allow update/merge
            if (existing.logics && existing.logics.length > 0) {
                return;
            }
            // If no logics, merge qty and update object
            const newQty = existing.qty + ref.qty;
            const cartItem = existing.cartItem ? { ...existing.cartItem, quantity: newQty } : ref.cartItem;
            console.log('Updating existing CartItemReference,cartItem:', cartItem);
            const updated: CartItemReference = {
                ...existing,
                qty: newQty,
                // CRITICAL: Sync cartItem with new quantity
                cartItem: cartItem,
            };
            setCartItemReferences(getCartItemReferences().map((r, i) => (i === idx ? updated : r)));
        } else {
            // Add new if not exists
            setCartItemReferences([...getCartItemReferences(), ref]);
        }
    };

    /**
     * Check if a CartItemReference has any gift action (promotion gift applied)
     */
    const isPromotionReference = (ref: CartItemReference): boolean => {
        return ref.logics.some(logic => logic.actions.some(action => action.actionType === ActionTypeEnum.GIFT));
    };

    /**
     * Update CartItemReference
     * Block qty/logic changes if has gift, but allow isSelected/cartId updates
     * CRITICAL: Always sync cartItems array when updating reference
     */
    const updateCartItemReference = (ref: CartItemReference) => {
        const existing = getCartItemReferences().find(r => r.cartItemReferenceId === ref.cartItemReferenceId);

        // If has promotion and trying to change qty or logics, block it
        if (existing && isPromotionReference(existing)) {
            // Check if only updating isSelected or cartId (allowed)
            const isOnlyMetadataUpdate =
                existing.qty === ref.qty &&
                existing.logics.length === ref.logics.length &&
                existing.productId === ref.productId;

            if (!isOnlyMetadataUpdate) {
                return;
            }
        }

        // CRITICAL: Sync cartItem with reference quantity
        const syncedRef = {
            ...ref,
            cartItem: ref.cartItem ? { ...ref.cartItem, quantity: ref.qty } : undefined,
        };

        setCartItemReferences(
            getCartItemReferences().map(r => (r && r.cartItemReferenceId === ref.cartItemReferenceId ? syncedRef : r))
        );
    };

    const removeCartItemReference = (cartItemReferenceId: string) => {
        setCartItemReferences(getCartItemReferences().filter(r => r && r.cartItemReferenceId !== cartItemReferenceId));
    };

    const areLogicsEqual = (a?: CartItemReferenceLogic[], b?: CartItemReferenceLogic[]): boolean => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        if (a.length !== b.length) return false;
        const aIds = a.map(l => l.logicId).sort();
        const bIds = b.map(l => l.logicId).sort();
        return aIds.every((id, idx) => id === bIds[idx]);
    };

    const findCartItemReference = (productId: number, logics?: CartItemReferenceLogic[]) => {
        return getCartItemReferences().find(
            r => r && r.productId === productId && (!logics || (r.logics && areLogicsEqual(r.logics, logics)))
        );
    };

    const initCartItemReference = (input: InitCartItemReferenceInput): CartItemReference => {
        const {
            productId,
            cartItemId,
            cartId,
            qty,
            logics,
            createdAt,
            cartItemReferenceId,
            isSelected,
            parentProduct,
            addToStore = true,
        } = input;
        const generatedCartItemReferenceId = cartItemReferenceId ?? uuidGenerator.generate();
        const now = createdAt || new Date().toISOString();

        // Get cartId from input or from store
        const resolvedCartId = cartId || cartStoreAPI.state.getCartId();

        const logicsWithIds: CartItemReferenceLogic[] = logics.map(logic => {
            const generatedLogicId = logic.id || uuidGenerator.generate();
            return {
                id: generatedLogicId,
                cartItemReferenceId: logic.cartItemReferenceId || generatedCartItemReferenceId,
                logicType: logic.logicType,
                logicId: logic.logicId,
                logicName: logic.logicName,
                allocatedQty: logic.allocatedQty,
                actions: logic.actions.map(action => ({
                    id: action.id || uuidGenerator.generate(),
                    cartItemReferenceLogicId: action.cartItemReferenceLogicId || generatedLogicId,
                    actionType: action.actionType,
                    actionProduct: action.actionProduct,
                    actionValue: action.actionValue ?? null,
                    actionNote: action.actionNote ?? null,
                    actionQty: action.actionQty ?? null,
                    createdAt: action.createdAt || now,
                })),
                createdAt: logic.createdAt || now,
                sessionId: logic.sessionId,
            };
        });

        const cartItemReference: CartItemReference = {
            cartItemReferenceId: generatedCartItemReferenceId,
            productId,
            cartId: resolvedCartId,
            cartItemId,
            qty,
            logics: logicsWithIds,
            isSelected: isSelected ?? false, // Use isSelected from input, default to false
            createdAt: now,
            parentProduct,
        };

        if (addToStore) {
            addCartItemReference(cartItemReference);
        }
        return cartItemReference;
    };

    const references$ = (): Observable<CartItemReference[]> => cartItemReferenceStoreAPI.state.getItemsObservable();

    const getCartItemReferencesWithCartItem = (cartItems: CartItemInterface[]): CartItemReference[] => {
        return getCartItemReferences().map(ref => ({
            ...ref,
            cartItem: cartItems.find(ci => ci.id === ref.cartItemId),
        }));
    };

    const getReferenceById = (cartItemReferenceId: string): CartItemReference | undefined => {
        return getCartItemReferences().find(ref => ref.cartItemReferenceId === cartItemReferenceId);
    };

    /**
     * Add logics to an existing cartItemReference by id
     */
    const addLogicsToCartItemReference = (cartItemReferenceId: string, newLogics: CartItemReferenceLogic[]) => {
        console.log('[useCartItemReference] addLogicsToCartItemReference:', {
            cartItemReferenceId,
            newLogics,
            currentReferences: getCartItemReferences(),
        });
        setCartItemReferences(
            getCartItemReferences().map((cartItemReference: CartItemReference) => {
                if (cartItemReference.cartItemReferenceId !== cartItemReferenceId) {
                    return cartItemReference;
                }
                // Merge: keep old logics not in new, add new logics by id
                const existingLogicIds = cartItemReference.logics.map(l => l.id);
                const filteredNewLogics = newLogics.filter(l => !existingLogicIds.includes(l.id));
                console.log('[useCartItemReference] Merging logics for reference:', {
                    cartItemReferenceId,
                    existingLogics: cartItemReference.logics,
                    filteredNewLogics,
                });
                return {
                    ...cartItemReference,
                    logics: [...cartItemReference.logics, ...filteredNewLogics],
                } as CartItemReference;
            })
        );
    };

    // Set selected/unselected for a cart item reference
    const setCartItemReferenceSelected = (cartItemReferenceId: string, selected: boolean) => {
        setCartItemReferences(
            getCartItemReferences().map(ref => {
                if (ref.cartItemReferenceId !== cartItemReferenceId) {
                    return ref;
                }

                // When unselecting, reset cart-related IDs to treat as new product
                if (!selected) {
                    return {
                        ...ref,
                        isSelected: false,
                        cartItem: ref.cartItem ? { ...ref.cartItem } : undefined,
                    };
                }

                // When selecting, just update the flag
                return {
                    ...ref,
                    isSelected: selected,
                    cartItem: ref.cartItem ? { ...ref.cartItem } : undefined,
                };
            })
        );
    };

    // Toggle selected state for a cart item reference
    const toggleCartItemReferenceSelected = (cartItemReferenceId: string) => {
        const existing = getCartItemReferences().find(r => r.cartItemReferenceId === cartItemReferenceId);
        if (!existing) {
            return;
        }

        const newSelected = !existing.isSelected;
        setCartItemReferenceSelected(cartItemReferenceId, newSelected);
    };

    // unselected state for all cart item reference
    const unselectedAllCartItemReferenceSelected = () => {
        setCartItemReferences(
            getCartItemReferences().map(ref => ({
                ...ref,
                isSelected: false,
                cartItem: ref.cartItem ? { ...ref.cartItem } : undefined,
            }))
        );
    };

    /**
     * Reset all cart item references when cart ID changes
     * This handles the case when user logs in/out or cart gets merged
     */
    const resetCartItemReferencesForNewCart = (newCartId: string) => {
        const currentReferences = getCartItemReferences();

        const resetReferences = currentReferences.map(ref => {
            // Reset cart item ID to treat as new product
            const resetCartItem = ref.cartItem
                ? {
                      ...ref.cartItem,
                      id:
                          ref.cartItem.id && !NewCartItemIdGenerator.isNewCartItemId(ref.cartItem.id)
                              ? NewCartItemIdGenerator.convertToNewId(ref.cartItem.id)
                              : ref.cartItem.id,
                  }
                : undefined;

            return {
                ...ref,
                cartId: newCartId, // Update to new cart ID
                cartItem: resetCartItem,
                // Reset cartItemId if it exists
                cartItemId: undefined,
            };
        });

        // Update store with new cart ID and reset references
        setCartItemReferences(resetReferences);
    };

    /**
     * Validate and fix cart item references for current cart
     * This should be called during app initialization or cart load
     */
    const validateAndFixCartItemReferences = (currentCartId: string) => {
        const currentReferences = getCartItemReferences();
        const storeCartId = cartStoreAPI.state.getCartId();

        // Case 1: Store cartId doesn't match current cart - reset all
        if (storeCartId && storeCartId !== currentCartId) {
            resetCartItemReferencesForNewCart(currentCartId);
            return;
        }

        // Case 3: Fix individual references without cartId
        const fixedReferences = currentReferences.map(ref => {
            if (!ref.cartId) {
                return { ...ref, cartId: currentCartId };
            }

            // Case 4: Reference has different cartId - reset to new product
            if (ref.cartId !== currentCartId) {
                const resetCartItem = ref.cartItem
                    ? {
                          ...ref.cartItem,
                          id: NewCartItemIdGenerator.generateFromReference(
                              ref.cartItem.product?.id ?? 0,
                              ref.parentProduct
                          ),
                      }
                    : undefined;

                return {
                    ...ref,
                    cartItem: resetCartItem,
                    cartItemId: undefined,
                };
            }

            return ref;
        });

        // Update if any changes were made
        const hasChanges = JSON.stringify(fixedReferences) !== JSON.stringify(currentReferences);
        if (hasChanges) {
            setCartItemReferences(fixedReferences);
        }
    };

    const getCartItemReferencesByProductId = (productId: number) => {
        const currentReferences = getCartItemReferences();
        return currentReferences.filter(ref => ref.productId === productId);
    };

    const stockAvailabilityMap = useSelector(() => cartStoreAPI.state.getAvailableItemsObservable().get());

    const checkAvailableToBuyCartItemReference = (cartItemReference: CartItemReference) => {
        const { cartItem } = cartItemReference;
        if (!cartItem) return false;

        const sku = getCartItemSku(cartItem);
        if (!sku) return false;

        return stockAvailabilityMap[sku]?.isSalable;
    };

    const getAvailableToBuyCartItemReferences = () => {
        const currentReferences = getCartItemReferences();
        return currentReferences.filter(cartItemReference => checkAvailableToBuyCartItemReference(cartItemReference));
    };

    const itemByProductIdWithoutGifts$ = useComputed<(productId: number) => CartItemLocalInterface | undefined>(() => {
        const refs = cartItemReferenceStoreAPI.state.getItemsObservable().get() ?? [];
        return (productId: number) => {
            const ref = refs.find(r => r?.cartItem?.product?.id === productId && (!r.logics || r.logics.length === 0));
            return ref?.cartItem;
        };
    });

    const getSelectedCartItem = () => {
        const currentReferences = getCartItemReferences();
        return currentReferences?.filter(item => item?.isSelected);
    };

    const hasSelectedLogics = () => {
        const selectedItems = getSelectedCartItem();
        return selectedItems?.some(item => item?.logics?.length > 0);
    };

    const removeSelectedLogics = () => {
        const currentReferences = getCartItemReferences();
        const itemsWithoutLogics = currentReferences?.map(item => {
            if (item?.isSelected) {
                return {
                    ...item,
                    logics: [],
                };
            }
            return item;
        });

        setCartItemReferences([...itemsWithoutLogics]);
    };

    const removeInvalidSelectedLogics = () => {
        const currentReferences = getCartItemReferences();
        const itemsWithoutInvalidLogics = currentReferences?.map(item => {
            if (!item?.isSelected) {
                return item;
            }

            return {
                ...item,
                logics: item?.logics?.map(logic => ({
                    ...logic,
                    actions: logic?.actions?.filter(
                        action => stockAvailabilityMap[action?.actionProduct?.sku ?? ''].isSalable
                    ),
                })),
            };
        });

        setCartItemReferences([...itemsWithoutInvalidLogics]);
    };

    const getCartItemsHasInvalidLogics = () => {
        const items = getSelectedCartItem();

        return items?.filter(item => {
            if (item?.logics?.length === 0) {
                return false;
            }

            return item?.logics?.some(logic =>
                logic?.actions?.some(
                    action => stockAvailabilityMap[action?.actionProduct?.sku ?? '']?.isSalable === false
                )
            );
        });
    };

    return {
        removeInvalidSelectedLogics,
        getCartItemsHasInvalidLogics,
        removeSelectedLogics,
        itemByProductIdWithoutGifts$,
        cartItemReferences: getCartItemReferences().filter(Boolean),
        addCartItemReference,
        updateCartItemReference,
        removeCartItemReference,
        findCartItemReference,
        setCartItemReferences,
        initCartItemReference,
        isPromotionReference: isPromotionReference,
        references$,
        getCartItemReferencesWithCartItems: getCartItemReferencesWithCartItem,
        getReferenceById,
        addLogicsToCartItemReference,
        setCartItemReferenceSelected,
        toggleCartItemReferenceSelected,
        unselectedAllCartItemReferenceSelected,
        resetCartItemReferencesForNewCart,
        validateAndFixCartItemReferences,
        getCartItemReferencesByProductId,
        checkAvailableToBuyCartItemReference,
        getAvailableToBuyCartItemReferences,
        hasSelectedLogics,
    };
};
